import { BadRequestException, Injectable, Logger, NotFoundException } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { TimeTravelEntity, cascadingChildren, entityFor } from './time-travel.catalog';

/** Above this, the merchant must type a confirmation rather than click one. */
const BULK_CONFIRMATION_THRESHOLD = 500;

export interface ChangeRow {
  id: string;
  tableName: string;
  recordId: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  correlationId: string | null;
  createdOn: Date;
  actorId: string | null;
}

export interface TimeTravelPreview {
  operationId: string;
  status: string;
  affectedRows: number;
  insertCount: number;
  updateCount: number;
  deleteCount: number;
  cascadeRows: number;
  conflictCount: number;
  protectedCount: number;
  expandedTables: string[];
  requiresTypedConfirmation: boolean;
  conflicts: ConflictRow[];
  protectedSkips: ProtectedRow[];
  sample: Array<{ table: string; recordId: string; operation: string; fields: string[] }>;
}

export interface ConflictRow {
  table: string;
  recordId: string;
  changedAt: string;
  changedBy: string | null;
  correlationId: string | null;
  fields: string[];
}

export interface ProtectedRow {
  table: string;
  recordId: string;
  reason: string;
}

/**
 * Time Travel — row-level undo, replayed from `audit.change_log` before-images.
 *
 * ── DEPENDENCY, STATED PLAINLY ───────────────────────────────────────────────
 *
 * This depends on plan-audit-changelog.md Stream A: triggers writing
 * to_jsonb(OLD)/to_jsonb(NEW) plus a correlationId for every mutation. Until
 * that lands, `preview` will correctly report almost nothing to revert, because
 * only 2 of 135 existing call sites record a before-image. That is the honest
 * failure mode and it is better than inventing a second before-image mechanism
 * to route around it — two competing audit trails is a worse outcome than a
 * delayed feature.
 *
 * ── WHY THE NAIVE VERSION IS WRONG ───────────────────────────────────────────
 *
 * "Replay matching rows in reverse timestamp order" corrupts data three ways:
 *
 *   - FK violations. Reverse-chronological is not FK order, and the two
 *     disagree constantly during an import that created parents and children in
 *     one transaction.
 *   - Half-reverted aggregates. A product whose price reverted but whose
 *     variants did not is not "partially recovered" — it is a NEW wrong state
 *     that never existed, and the merchant can no longer tell what is real.
 *   - Silent third-party clobber. Reverting to 14:00 discards whatever someone
 *     else did at 15:00, with no warning.
 *
 * So this operates on AGGREGATES in topological order, detects conflicts and
 * refuses rather than resolving them, and verifies invariants before COMMIT.
 * Because the schema is FK-mixed (see the catalog), the database will not catch
 * a mistake here — that pre-COMMIT check is the only safety net.
 */
@Injectable()
export class TimeTravelService {
  private readonly logger = new Logger(TimeTravelService.name);

  constructor(private readonly prisma: PrismaService) {}

  /**
   * Build a preview. Nothing is written to merchant data here.
   *
   * The operation is persisted as `previewed`, and only an explicit `apply`
   * with that id can act on it — so what the merchant approves is exactly what
   * runs, rather than a fresh computation that may have drifted since they
   * looked at it.
   */
  async preview(input: {
    organizationId: string;
    storeId?: string;
    scope: 'row' | 'entity' | 'bulk_operation' | 'table' | 'store';
    entityType?: string;
    entityIds?: string[];
    correlationId?: string;
    restoreToAt: Date;
    requestedBy: string;
    requestedByEmail?: string;
  }): Promise<TimeTravelPreview> {
    const entity = input.entityType ? entityFor(input.entityType) : undefined;

    if (input.entityType && !entity) {
      // Fail closed. A table nobody has catalogued has unknown children, and
      // reverting an aggregate with a missing child produces corruption that
      // nothing downstream can detect.
      throw new BadRequestException(
        `"${input.entityType}" is not in the Time Travel catalog, so it cannot be reverted. ` +
          `Add it to TIME_TRAVEL_ENTITIES (with every child table) or to NOT_TIME_TRAVELLABLE.`
      );
    }

    if (entity?.protectedBy.includes('*')) {
      throw new BadRequestException(entity.refuseReason ?? `${entity.model} cannot be time-travelled.`);
    }

    const changes = await this.collectChanges(input);
    if (!changes.length) {
      const empty = await this.persistPreview(input, {
        rows: [],
        conflicts: [],
        protectedSkips: [],
        cascadeRows: 0,
        expandedTables: [],
      });
      return empty;
    }

    const expanded = entity ? await this.expand(entity, changes, input) : changes;
    const conflicts = await this.detectConflicts(expanded, input.restoreToAt, input.correlationId);
    const protectedSkips = entity ? await this.detectProtected(entity, expanded) : [];

    // Cascade rows are counted but not planned: Postgres removes them for us on
    // the enforced relationships. They still belong in the total, because the
    // merchant is approving a blast radius, not a statement list.
    const cascadeRows = entity ? await this.countCascades(entity, expanded) : 0;

    const protectedIds = new Set(protectedSkips.map((p) => `${p.table}:${p.recordId}`));
    const actionable = expanded.filter((c) => !protectedIds.has(`${c.tableName}:${c.recordId}`));

    return this.persistPreview(input, {
      rows: actionable,
      conflicts,
      protectedSkips,
      cascadeRows,
      expandedTables: [...new Set(actionable.map((c) => c.tableName))],
    });
  }

  /**
   * Everything a query says was changed after `restoreToAt`.
   *
   * `correlationId` is the high-value path — "undo that CSV import" is one
   * indexed lookup rather than a scan — which is the whole reason the audit
   * plan's correlation propagation matters commercially and not just tidily.
   */
  private async collectChanges(input: {
    organizationId: string;
    storeId?: string;
    scope: string;
    entityType?: string;
    entityIds?: string[];
    correlationId?: string;
    restoreToAt: Date;
  }): Promise<ChangeRow[]> {
    const conditions: string[] = [];
    const params: unknown[] = [];

    if (input.correlationId) {
      params.push(input.correlationId);
      conditions.push(`"correlationId" = $${params.length}`);
    } else {
      params.push(input.restoreToAt);
      conditions.push(`"createdOn" > $${params.length}`);

      if (input.storeId) {
        params.push(input.storeId);
        conditions.push(`"storeId" = $${params.length}`);
      }
      if (input.entityIds?.length) {
        params.push(input.entityIds);
        conditions.push(`"recordId" = ANY($${params.length})`);
      }
    }

    // Raw because change_log is written by triggers and read here by shape, not
    // through the Prisma model — and because the correlation/time predicates
    // want the composite indexes the audit plan defines.
    const rows = await this.prisma.$queryRawUnsafe<ChangeRow[]>(
      `SELECT "id", "tableName", "recordId", "operation", "oldValue", "newValue",
              "correlationId", "createdOn", "actorId"
         FROM audit.change_log
        WHERE ${conditions.join(' AND ')}
        ORDER BY "createdOn" DESC
        LIMIT 50000`,
      ...params
    );

    return rows;
  }

  /**
   * Pull in every child row of each touched aggregate root.
   *
   * Without this, reverting "a product" reverts the products row and leaves its
   * variants, images and inventory at the new values — the half-reverted state
   * described in the class comment.
   */
  private async expand(
    entity: TimeTravelEntity,
    changes: ChangeRow[],
    input: { restoreToAt: Date; correlationId?: string }
  ): Promise<ChangeRow[]> {
    const rootIds = changes.filter((c) => c.tableName === entity.rootTable).map((c) => c.recordId);
    if (!rootIds.length) return changes;

    if (entity.selfReference) {
      this.assertNoCycle(entity, changes);
    }

    const childTables = entity.children.map((c) => c.table);
    const extra = await this.prisma.$queryRawUnsafe<ChangeRow[]>(
      `SELECT "id", "tableName", "recordId", "operation", "oldValue", "newValue",
              "correlationId", "createdOn", "actorId"
         FROM audit.change_log
        WHERE "tableName" = ANY($1)
          AND "createdOn" > $2
        ORDER BY "createdOn" DESC
        LIMIT 50000`,
      childTables,
      input.restoreToAt
    );

    const seen = new Set(changes.map((c) => c.id));
    return [...changes, ...extra.filter((c) => !seen.has(c.id))];
  }

  /**
   * Refuse a re-parenting revert that would produce a cycle.
   *
   * Category.parentId is self-referential. A depth-based sort does not
   * terminate on a cycle, and a revert of a re-parenting is exactly how one
   * gets created — A was moved under B, B was moved under A, revert both and
   * they point at each other.
   */
  private assertNoCycle(entity: TimeTravelEntity, changes: ChangeRow[]): void {
    const parentOf = new Map<string, string>();
    for (const change of changes) {
      if (change.tableName !== entity.rootTable) continue;
      const parent = (change.oldValue as Record<string, unknown> | null)?.[entity.selfReference as string];
      if (typeof parent === 'string') parentOf.set(change.recordId, parent);
    }

    for (const start of parentOf.keys()) {
      const seen = new Set<string>([start]);
      let cursor = parentOf.get(start);
      while (cursor) {
        if (seen.has(cursor)) {
          throw new BadRequestException(
            `Reverting this would make ${entity.model} records their own ancestors. ` +
              `Revert the re-parenting separately, then retry.`
          );
        }
        seen.add(cursor);
        cursor = parentOf.get(cursor);
      }
    }
  }

  /**
   * Rows changed after `restoreToAt` by something other than this operation.
   *
   * Surfaced, never resolved. Auto-including discards a third party's work;
   * auto-skipping produces a half-reverted aggregate. Both are wrong, so the
   * only correct behaviour is to make a human look.
   */
  private async detectConflicts(
    changes: ChangeRow[],
    restoreToAt: Date,
    correlationId?: string
  ): Promise<ConflictRow[]> {
    if (!changes.length) return [];

    const recordIds = [...new Set(changes.map((c) => c.recordId))];
    const later = await this.prisma.$queryRawUnsafe<ChangeRow[]>(
      `SELECT DISTINCT ON ("tableName", "recordId")
              "id", "tableName", "recordId", "operation", "oldValue", "newValue",
              "correlationId", "createdOn", "actorId"
         FROM audit.change_log
        WHERE "recordId" = ANY($1)
          AND "createdOn" > $2
        ORDER BY "tableName", "recordId", "createdOn" DESC`,
      recordIds,
      restoreToAt
    );

    return later
      .filter((row) => !correlationId || row.correlationId !== correlationId)
      .map((row) => ({
        table: row.tableName,
        recordId: row.recordId,
        changedAt: row.createdOn.toISOString(),
        changedBy: row.actorId,
        correlationId: row.correlationId,
        fields: this.changedFields(row.oldValue, row.newValue),
      }));
  }

  /**
   * Rows the expansion reached but must not touch.
   *
   * Shown to the merchant as "not reverted, and here is why" rather than
   * hidden. Silent omission is how someone concludes the undo worked when it
   * partly did not.
   */
  private async detectProtected(entity: TimeTravelEntity, changes: ChangeRow[]): Promise<ProtectedRow[]> {
    if (!entity.protectedBy.length) return [];

    const out: ProtectedRow[] = [];
    const rootIds = changes.filter((c) => c.tableName === entity.rootTable).map((c) => c.recordId);
    if (!rootIds.length) return out;

    for (const reference of entity.protectedBy) {
      const [table, column] = reference.split('.');
      if (!table || !column) continue;

      const hits = await this.prisma.$queryRawUnsafe<Array<{ ref: string }>>(
        `SELECT DISTINCT "${column}" AS ref FROM sales."${table}" WHERE "${column}" = ANY($1)`,
        rootIds
      );

      for (const hit of hits) {
        out.push({
          table: entity.rootTable,
          recordId: hit.ref,
          reason:
            entity.refuseReason ??
            `Referenced by ${table}. Time Travel restores catalog and content, never financial records.`,
        });
      }
    }

    return out;
  }

  /** Rows Postgres will delete for us, counted so the preview total is honest. */
  private async countCascades(entity: TimeTravelEntity, changes: ChangeRow[]): Promise<number> {
    const cascading = cascadingChildren(entity);
    if (!cascading.length) return 0;

    // Only INSERT reverts turn into DELETEs, so only those trigger cascades.
    const toDelete = changes.filter((c) => c.tableName === entity.rootTable && c.operation === 'INSERT');
    if (!toDelete.length) return 0;

    let total = 0;
    for (const child of cascading) {
      const rows = await this.prisma.$queryRawUnsafe<Array<{ count: bigint }>>(
        `SELECT count(*)::bigint AS count FROM products."${child.table}" WHERE "${child.fk}" = ANY($1)`,
        toDelete.map((c) => c.recordId)
      );
      total += Number(rows[0]?.count ?? 0);
    }
    return total;
  }

  private async persistPreview(
    input: {
      organizationId: string;
      storeId?: string;
      scope: string;
      entityType?: string;
      entityIds?: string[];
      correlationId?: string;
      restoreToAt: Date;
      requestedBy: string;
      requestedByEmail?: string;
    },
    computed: {
      rows: ChangeRow[];
      conflicts: ConflictRow[];
      protectedSkips: ProtectedRow[];
      cascadeRows: number;
      expandedTables: string[];
    }
  ): Promise<TimeTravelPreview> {
    const insertCount = computed.rows.filter((r) => r.operation === 'DELETE').length;
    const deleteCount = computed.rows.filter((r) => r.operation === 'INSERT').length;
    const updateCount = computed.rows.filter((r) => r.operation === 'UPDATE').length;

    const status = computed.conflicts.length
      ? 'blocked_conflict'
      : computed.rows.length === 0 && computed.protectedSkips.length
        ? 'blocked_protected'
        : 'previewed';

    const sample = computed.rows.slice(0, 25).map((r) => ({
      table: r.tableName,
      recordId: r.recordId,
      operation: r.operation,
      fields: this.changedFields(r.oldValue, r.newValue),
    }));

    const operation = await this.prisma.timeTravelOperation.create({
      data: {
        organizationId: input.organizationId,
        storeId: input.storeId,
        scope: input.scope,
        entityType: input.entityType,
        entityIds: input.entityIds ?? [],
        expandedTables: computed.expandedTables,
        correlationId: input.correlationId,
        restoreToAt: input.restoreToAt,
        status,
        affectedRows: computed.rows.length,
        insertCount,
        updateCount,
        deleteCount,
        cascadeRows: computed.cascadeRows,
        preview: { sample } as never,
        conflicts: computed.conflicts as never,
        conflictCount: computed.conflicts.length,
        protectedSkips: computed.protectedSkips as never,
        protectedCount: computed.protectedSkips.length,
        requestedBy: input.requestedBy,
        requestedByEmail: input.requestedByEmail,
      },
    });

    return {
      operationId: operation.id,
      status,
      affectedRows: computed.rows.length,
      insertCount,
      updateCount,
      deleteCount,
      cascadeRows: computed.cascadeRows,
      conflictCount: computed.conflicts.length,
      protectedCount: computed.protectedSkips.length,
      expandedTables: computed.expandedTables,
      // The total the merchant is agreeing to includes cascades, which is the
      // number that would otherwise surprise them.
      requiresTypedConfirmation: computed.rows.length + computed.cascadeRows > BULK_CONFIRMATION_THRESHOLD,
      conflicts: computed.conflicts,
      protectedSkips: computed.protectedSkips,
      sample,
    };
  }

  private changedFields(
    oldValue: Record<string, unknown> | null,
    newValue: Record<string, unknown> | null
  ): string[] {
    if (!oldValue || !newValue) return [];
    const keys = new Set([...Object.keys(oldValue), ...Object.keys(newValue)]);
    return [...keys].filter((k) => JSON.stringify(oldValue[k]) !== JSON.stringify(newValue[k]));
  }

  /**
   * Apply a previously previewed operation.
   *
   * One transaction. Statements ordered by FK depth. Invariants checked before
   * COMMIT, and any failure rolls the whole thing back — an undo that commits a
   * broken aggregate has turned a recoverable problem into an unrecoverable
   * one, with the merchant's explicit blessing, which is the worst possible way
   * to lose someone's data.
   */
  async apply(operationId: string, actorId: string): Promise<{ applied: boolean; affectedRows: number }> {
    const operation = await this.prisma.timeTravelOperation.findUnique({ where: { id: operationId } });
    if (!operation) throw new NotFoundException('Time Travel operation not found');

    if (operation.status !== 'previewed') {
      throw new BadRequestException(
        `This operation is "${operation.status}" and cannot be applied. ` +
          (operation.status === 'blocked_conflict'
            ? 'Resolve the listed conflicts and preview again.'
            : 'Only a previewed operation can be applied.')
      );
    }

    const entity = operation.entityType ? entityFor(operation.entityType) : undefined;

    await this.prisma.timeTravelOperation.update({
      where: { id: operationId },
      data: { status: 'applying', approvedBy: actorId },
    });

    const started = Date.now();

    try {
      const changes = await this.collectChanges({
        organizationId: operation.organizationId,
        storeId: operation.storeId ?? undefined,
        scope: operation.scope,
        entityType: operation.entityType ?? undefined,
        entityIds: operation.entityIds,
        correlationId: operation.correlationId ?? undefined,
        restoreToAt: operation.restoreToAt,
      });

      await this.prisma.$transaction(async (tx) => {
        // Helps only the declared-FK minority, but costs nothing and removes a
        // whole class of ordering edge case where it does apply.
        await tx.$executeRawUnsafe('SET CONSTRAINTS ALL DEFERRED');

        const plan = this.orderStatements(entity, changes);

        for (const change of plan) {
          if (change.operation === 'UPDATE' && change.oldValue) {
            await this.writeUpdate(tx, change);
          } else if (change.operation === 'DELETE' && change.oldValue) {
            await this.writeInsert(tx, change);
          } else if (change.operation === 'INSERT') {
            await this.writeDelete(tx, change);
          }
        }

        // Pre-COMMIT verification. With most child links unenforced, this is
        // the only thing standing between a bad expansion and corrupt data.
        if (entity) await this.verifyInvariants(tx, entity, changes);
      });

      await this.prisma.timeTravelOperation.update({
        where: { id: operationId },
        data: { status: 'applied', appliedAt: new Date(), durationMs: Date.now() - started },
      });

      return { applied: true, affectedRows: operation.affectedRows };
    } catch (err) {
      await this.prisma.timeTravelOperation.update({
        where: { id: operationId },
        data: { status: 'failed', error: (err as Error).message, durationMs: Date.now() - started },
      });
      throw err;
    }
  }

  /**
   * Topological ordering.
   *
   * INSERTs parents-first, DELETEs children-first, UPDATEs anywhere. Derived
   * from the catalog rather than hand-written per aggregate, because a
   * hand-written order is one refactor away from being subtly wrong in a way
   * only unenforced FKs would let through.
   */
  private orderStatements(entity: TimeTravelEntity | undefined, changes: ChangeRow[]): ChangeRow[] {
    if (!entity) return changes;

    const depthOf = new Map<string, number>([[entity.rootTable, 0]]);
    for (const child of entity.children) depthOf.set(child.table, child.depth);

    const inserts = changes.filter((c) => c.operation === 'DELETE');
    const deletes = changes.filter((c) => c.operation === 'INSERT');
    const updates = changes.filter((c) => c.operation === 'UPDATE');

    const byDepth = (dir: 1 | -1) => (a: ChangeRow, b: ChangeRow) =>
      dir * ((depthOf.get(a.tableName) ?? 99) - (depthOf.get(b.tableName) ?? 99));

    // A table the catalog does not know sorts to depth 99, i.e. last on insert
    // and first on delete. That is the safe end in both directions: an unknown
    // child is written after everything it might reference, and removed before
    // anything that might reference it.
    return [
      // Reverting a DELETE means re-INSERTing: parents first (depth ascending).
      ...inserts.sort(byDepth(1)),
      ...updates,
      // Reverting an INSERT means DELETEing: children first (depth descending).
      ...deletes.sort(byDepth(-1)),
    ];
  }

  private async writeUpdate(tx: unknown, change: ChangeRow): Promise<void> {
    const columns = Object.keys(change.oldValue ?? {}).filter((k) => k !== 'id');
    if (!columns.length) return;

    const assignments = columns.map((c, i) => `"${c}" = $${i + 1}`).join(', ');
    const values = columns.map((c) => (change.oldValue as Record<string, unknown>)[c]);

    await (tx as { $executeRawUnsafe: (q: string, ...p: unknown[]) => Promise<number> }).$executeRawUnsafe(
      `UPDATE "${change.tableName}" SET ${assignments} WHERE "id" = $${columns.length + 1}`,
      ...values,
      change.recordId
    );
  }

  private async writeInsert(tx: unknown, change: ChangeRow): Promise<void> {
    const row = change.oldValue as Record<string, unknown>;
    const columns = Object.keys(row);
    const placeholders = columns.map((_, i) => `$${i + 1}`).join(', ');

    await (tx as { $executeRawUnsafe: (q: string, ...p: unknown[]) => Promise<number> }).$executeRawUnsafe(
      `INSERT INTO "${change.tableName}" (${columns.map((c) => `"${c}"`).join(', ')})
       VALUES (${placeholders}) ON CONFLICT ("id") DO NOTHING`,
      ...columns.map((c) => row[c])
    );
  }

  private async writeDelete(tx: unknown, change: ChangeRow): Promise<void> {
    await (tx as { $executeRawUnsafe: (q: string, ...p: unknown[]) => Promise<number> }).$executeRawUnsafe(
      `DELETE FROM "${change.tableName}" WHERE "id" = $1`,
      change.recordId
    );
  }

  /**
   * Aggregate invariants, checked inside the transaction.
   *
   * Deliberately cheap and specific rather than a general integrity sweep: the
   * question is "is THIS aggregate coherent after THIS undo", and a broad check
   * would be too slow to run inline and too vague to act on.
   */
  private async verifyInvariants(tx: unknown, entity: TimeTravelEntity, changes: ChangeRow[]): Promise<void> {
    const rootIds = [...new Set(changes.filter((c) => c.tableName === entity.rootTable).map((c) => c.recordId))];
    if (!rootIds.length) return;

    const exec = tx as { $queryRawUnsafe: <T>(q: string, ...p: unknown[]) => Promise<T> };

    for (const child of entity.children) {
      if (child.via) continue; // indirect children are covered by their parent

      // Orphan check. On the unenforced links this is the ONLY thing that would
      // ever notice; on the enforced ones the FK would have thrown already.
      const orphans = await exec.$queryRawUnsafe<Array<{ count: bigint }>>(
        `SELECT count(*)::bigint AS count
           FROM "${child.table}" c
          WHERE c."${child.fk}" = ANY($1)
            AND NOT EXISTS (SELECT 1 FROM "${entity.rootTable}" p WHERE p."id" = c."${child.fk}")`,
        rootIds
      );

      const count = Number(orphans[0]?.count ?? 0);
      if (count > 0) {
        throw new BadRequestException(
          `Undo aborted: it would leave ${count} orphaned row(s) in ${child.table}. ` +
            `Nothing was changed. This usually means the catalog is missing a table or a depth is wrong.`
        );
      }
    }
  }
}
